// board.js — S5: block scan + list columns.
//
// Fetchers: paginated getBlocks (drag source), getList per canonical list
// (columns), getLists filtered to the canonical four (name->uri map).
// Pure helpers (tested): canonical order, membership checks.

import { LIST_DEFS } from './lists';

// ---- Pure helpers ----

// Filter an account's lists down to the canonical four, in LIST_DEFS
// order, returning [{ name, uri }]. Non-canonical lists are ignored.
export function canonicalLists(lists) {
  const byName = new Map(lists.map((l) => [l.name, l]));
  const out = [];

  for (const def of LIST_DEFS) {
    const hit = byName.get(def.name);

    if (hit && hit.uri) out.push({ name: def.name, uri: hit.uri });
  }

  return out;
}

// ListItemView[] -> does the column already contain this DID?
export function itemsContainDid(items, did) {
  return items.some((item) => item.subject?.did === did);
}

// ---- Paginated fetchers (agent-bound) ----

// Blocks carry the user's block-record at-uri via viewer.blocking (the
// authenticated view) so an add can unblock the raw record (refinement
// 2026-09-07: list membership takes over from the individual block).
export async function fetchAllBlocks(agent) {
  const blocks = [];
  let cursor;

  do {
    const res = await agent.app.bsky.graph.getBlocks({ limit: 100, cursor });

    for (const block of res.data.blocks) {
      blocks.push({ ...block, blockUri: block.viewer?.blocking ?? null });
    }

    cursor = res.data.cursor;
  } while (cursor);

  return blocks;
}

export async function fetchListItems(agent, listUri) {
  const items = [];
  let cursor;

  do {
    const res = await agent.app.bsky.graph.getList({ list: listUri, limit: 100, cursor });
    items.push(...res.data.items);
    cursor = res.data.cursor;
  } while (cursor);

  return items;
}

// Returns the account's canonical lists [{name, uri}] (in LIST_DEFS
// order). Requires the S4 ensure step to have run first so all four exist.
// Retries briefly: right after creating lists, the PDS/AppView index can
// lag (eventual consistency) and a fresh getLists may miss a just-created
// list — which surfaces as "Expected 4 canonical lists, found 3".
export async function fetchCanonicalLists(agent, did, { retries = 3, delayMs = 600 } = {}) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const lists = [];
    let cursor;

    do {
      const res = await agent.app.bsky.graph.getLists({ actor: did, limit: 100, cursor });
      lists.push(...res.data.lists);
      cursor = res.data.cursor;
    } while (cursor);

    const canonical = canonicalLists(lists);

    if (canonical.length === LIST_DEFS.length) return canonical;

    if (attempt < retries) {
      await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)));
    } else {
      return canonical; // give up; caller surfaces the count mismatch
    }
  }

  return canonicalLists([]);
}

// Add a block DID to a list. createdAt = now (the timer — plan §5b).
// repoDid = the signed-in account (writes go through its PDS);
// subjectDid = the blocked account being added.
// Returns the created listitem view {uri, subject}.
export async function addListItem(agent, repoDid, listUri, subjectDid, subjectProfile) {
  const res = await agent.com.atproto.repo.createRecord({
    repo: repoDid,
    collection: 'app.bsky.graph.listitem',
    record: {
      subject: subjectDid,
      list: listUri,
      createdAt: new Date().toISOString(),
    },
  });

  return { uri: res.data.uri, subject: subjectProfile };
}

// ---- S7: removal ----

// AT-URI -> rkey (last path segment). at://did/app.bsky.graph.listitem/tid
// -> 'tid'. Returns null for malformed input.
export function rkeyFromUri(uri) {
  // Boundary parse: uri is an at-uri string or null from the API.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof uri !== 'string') return null;
  const slash = uri.lastIndexOf('/');

  if (slash === -1 || slash === uri.length - 1) return null;

  return uri.slice(slash + 1);
}

// Remove an account from a list by deleting its listitem record.
export async function removeListItem(agent, did, listitemUri) {
  const rkey = rkeyFromUri(listitemUri);

  if (!rkey) throw new Error(`Invalid listitem URI: ${listitemUri}`);
  await agent.com.atproto.repo.deleteRecord({
    repo: did,
    collection: 'app.bsky.graph.listitem',
    rkey,
  });
}

// ---- Pardon (2026-09-07): remove all blocks + all list memberships ----

// Fetch every block record (via getBlocks viewer.blocking) and every
// listitem across the canonical lists. Returns { blockUris, listitemUris }.
export async function fetchAllBlockAndListItemUris(agent, did) {
  const blocks = await fetchAllBlocks(agent);

  const blockUris = blocks
      .map((b) => b.blockUri)
      .filter((uri) => uri !== undefined && uri !== null && uri.length > 0);

  const canonical = await fetchCanonicalLists(agent, did);
  const listitemUris = [];

  for (const list of canonical) {
    const items = await fetchListItems(agent, list.uri);

    for (const item of items) listitemUris.push(item.uri);
  }

  return { blockUris, listitemUris };
}

// Pardon: delete every block record and every listitem record on the
// account. Sequential + bounded so the PDS isn't hammered. Returns the
// counts deleted.
export async function pardonAll(agent, did, { onProgress } = {}) {
  const { blockUris, listitemUris } = await fetchAllBlockAndListItemUris(agent, did);

  let blocksDeleted = 0;

  for (const uri of blockUris) {
    await deleteBlockRecord(agent, did, uri);
    blocksDeleted++;
    onProgress?.(`Unblocking ${blocksDeleted}/${blockUris.length}...`);
  }

  let itemsDeleted = 0;

  for (const uri of listitemUris) {
    await removeListItem(agent, did, uri);
    itemsDeleted++;
    onProgress?.(`Removing list members ${itemsDeleted}/${listitemUris.length}...`);
  }

  return { blocksDeleted, itemsDeleted };
}

// ---- Feature 1 (2026-09-07): deleted-account block cleanup ----

// Check whether a DID still resolves at plc.directory. Deleted accounts
// return 404 (did doc gone). One HTTP lookup per DID.
async function didResolves(did) {
  try {
    const res = await fetch(`https://plc.directory/${encodeURIComponent(did)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    return res.status === 200;
  } catch {
    return false;
  }
}

// Check a batch of DIDs concurrently, returning the set that no longer
// resolves (deleted accounts). Used on scan to flag deletable block
// records.
export async function findDeletedDids(dids) {
  const unique = [...new Set(dids)];

  const results = await Promise.all(
    unique.map(async (did) => ({ did, alive: await didResolves(did) }))
  );

  return results.filter((r) => !r.alive).map((r) => r.did);
}

// Delete an individual block record (the blockUri from viewer.blocking).
export async function deleteBlockRecord(agent, did, blockUri) {
  const rkey = rkeyFromUri(blockUri);

  if (!rkey) throw new Error(`Invalid block URI: ${blockUri}`);
  await agent.com.atproto.repo.deleteRecord({
    repo: did,
    collection: 'app.bsky.graph.block',
    rkey,
  });
}

// ---- Refinement 2026-09-07: unblock on list-add ----

// Delete the user's individual block record so the moderation LIST takes
// over (the block at-uri comes from viewer.blocking on getBlocks).
export async function unblockAccount(agent, did, blockUri) {
  const rkey = rkeyFromUri(blockUri);

  if (!rkey) throw new Error(`Invalid block URI: ${blockUri}`);
  await agent.com.atproto.repo.deleteRecord({
    repo: did,
    collection: 'app.bsky.graph.block',
    rkey,
  });
}

